import React, { useCallback, useState } from 'react';
import classnames from 'classnames';

import { format } from '@msdyn365-commerce-modules/utilities';

import { WishlistData } from './cus-wishlist.data';
import { ICusWishlistProps } from './cus-wishlist.props.autogenerated';
import { WISHLIST_IMAGE_VIEWPORT_SETTINGS, WISHLIST_MODULE_NAME } from './cus-wishlist.const';
import { EmptyWishlist } from './components/empty-wishlist/empty-wishlist';
import { WishlistError } from './components/wishlist-error/wishlist-error';
import { WishlistItem } from './components/wishlist-item/wishlist-item';
import { WishlistLoggedOut } from './components/wishlist-logged-out/wishlist-logged-out';
import { MappedProduct } from '../../integrations/relewise/types/mapped';
import { AddToCartDrawer } from '../../shared/components/add-to-cart-drawer/add-to-cart-drawer';
import { ColumnCount, ColumnToggle } from '../../shared/components/column-toggle/column-toggle';
import { useImageSettings } from '../../shared/hooks/useImageSettings';
import { useRemoveFromWishlist } from './hooks/useRemoveFromWishlist';

type Props = ICusWishlistProps<WishlistData>;

const Wishlist = (props: Props) => {
    const imageSettings = useImageSettings(WISHLIST_IMAGE_VIEWPORT_SETTINGS);
    const removeFromWishlist = useRemoveFromWishlist(props.data?.wishlistData?.result?.wishlist, props.context, WISHLIST_MODULE_NAME);

    const [removedVariantIds, setRemovedVariantIds] = useState<Set<number>>(new Set());
    const [isBusy, setIsBusy] = useState(false);
    const [mobileColumns, setMobileColumns] = useState<ColumnCount>(2);
    const [addToCartProduct, setAddToCartProduct] = useState<MappedProduct>();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const products = (props.data?.wishlistData?.result?.products ?? []).filter(
        product => product.variantRecordId === undefined || !removedVariantIds.has(product.variantRecordId)
    );

    const handleRemove = useCallback(
        async (product: MappedProduct): Promise<{ success: boolean }> => {
            const { variantRecordId, relewiseProductId, relewiseVariantId } = product;

            setIsBusy(true);
            const { success } = await removeFromWishlist({ variantRecordId, relewiseProductId, relewiseVariantId });
            if (success && variantRecordId !== undefined) {
                setRemovedVariantIds(prev => new Set(prev).add(variantRecordId));
            }
            setIsBusy(false);
            return { success };
        },
        [removeFromWishlist]
    );

    const getFallbackState = () => {
        if (!props.context.request.user.isAuthenticated) {
            return <WishlistLoggedOut resources={props.resources} context={props.context} />;
        }
        if (props.data?.wishlistData?.result?.hasError) {
            return <WishlistError resources={props.resources} />;
        }
        if (!products.length) {
            return <EmptyWishlist resources={props.resources} context={props.context} />;
        }
        return undefined;
    };

    const fallbackState = getFallbackState();

    return (
        <section className='bs-wishlist'>
            <h1 className='bs-wishlist__title'>{props.resources.defaultWishlistName}</h1>
            {fallbackState ?? (
                <>
                    <p className='bs-wishlist__subtitle'>{format(props.resources.accountWishlistTileDescription, products.length)}</p>
                    <div className='bs-wishlist__toolbar'>
                        <ColumnToggle
                            singleColumnViewAriaLabel={props.resources.singleColumnViewAriaLabel}
                            twoColumnViewAriaLabel={props.resources.twoColumnViewAriaLabel}
                            columns={mobileColumns}
                            showAtBreakPoint='small'
                            onChange={setMobileColumns}
                        />
                    </div>
                    <ul className={classnames('bs-wishlist__list', { 'bs-wishlist__list--single-column': mobileColumns === 1 })}>
                        {products.map(product => (
                            <WishlistItem
                                key={product.relewiseVariantId}
                                product={product}
                                imageSettings={imageSettings}
                                gridSettings={props.context.request.gridSettings}
                                resources={props.resources}
                                telemetry={props.context.telemetry}
                                hideCartButton={!!props.config.hideCartButton}
                                isBusy={isBusy}
                                onRemove={handleRemove}
                                onAddToCart={product => {
                                    setAddToCartProduct(product);
                                    setIsDrawerOpen(true);
                                }}
                            />
                        ))}
                    </ul>
                    <AddToCartDrawer
                        masterProductRecordId={addToCartProduct?.productRecordId}
                        productName={addToCartProduct?.displayName ?? ''}
                        isOpen={isDrawerOpen}
                        selectedColor={addToCartProduct?.color?.name}
                        onClose={() => setIsDrawerOpen(false)}
                        context={props.context}
                        labels={{
                            title: props.resources.addToCartDrawerTitle,
                            closeButtonLabel: props.resources.closeButtonLabel,
                            colorSelectionLabel: props.resources.productDimensionTypeColor,
                            selectSizeLabel: props.resources.sizeSelectionHeading,
                            outOfStockLabel: props.resources.outOfStockText,
                            addToCartLabel: props.resources.addToCartText,
                            goToProductLabel: props.resources.goToProductText,
                            addToCartErrorLabel: props.resources.addedToCartFailureMessage,
                            loadingErrorLabel: props.resources.addToCartDrawerLoadingErrorText
                        }}
                    />
                </>
            )}
        </section>
    );
};

export default Wishlist;
